// @ts-nocheck

import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { BasketIcon as BasketIconPath } from '../../assets/img/betting';
import { Target } from '../../client';
import { usePrevious } from '../../hooks';
import { __ } from '../../i18n/l10n';
import NumberFormatter from '../../i18n/numberformatter';
import theme from '../../styled-components/dark';
import { ZIndices } from '../../styled-components/dark/common';
import AnimatedDigitsLabel from '../AnimatedDigitsLabel';

const MARGIN = 1; // em

const APPERANCE_TIMEOUT = 300;

const StyleReceiver = styled.div`
  position: fixed;
  width: 4.5em;
  height: 4em;

  transform: translate3d(0, 0, 0);
  z-index: ${ZIndices.floatingBasketButton};

  bottom: calc(3.6em + ${MARGIN}em);
  bottom: calc(3.6em + constant(safe-area-inset-bottom) + ${MARGIN}em);
  bottom: calc(3.6em + env(safe-area-inset-bottom) + ${MARGIN}em);

  right: ${MARGIN}em;

  ${({ $show }) =>
    !$show
      ? css`
          pointer-events: none;
        `
      : undefined}
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: ${theme.FloatingBasketButton.backgroundColor};
  border-radius: 1.3em;
  filter: drop-shadow(0 0 1em black);
  transition:
    transform 150ms ease-in-out,
    opacity ${APPERANCE_TIMEOUT}ms ease-in-out;
  user-select: none;

  ${Target.__MOBILE__
    ? css`
        &:active {
          transform: scale(0.95);
        }
      `
    : undefined}

  ${({ $show }) => css`
    opacity: ${$show ? 1 : 0};
  `}
`;

const NbItemsCounter = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 33%;
  padding-bottom: 33%;
  transform: translate(20%, -20%);
  border-radius: 50%;

  background: white;

  &:after {
    ${({ $n }) => css`
      content: '${$n > 99 ? '99+' : $n}';
    `}
    position: absolute;
    color: black;
    font-weight: bold;
    font-size: 0.85em;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
`;

const BasketIcon = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -95%);
  background-image: url(${BasketIconPath});
  height: 1.8em;
  width: 1.8em;
  background-repeat: no-repeat;
  background-size: contain;
`;

const oddsLabelStyle = css`
  position: absolute;
  bottom: 7%;
  left: 0;
  width: 100%;
  padding: 0 0.4em;

  text-align: center;
  font-size: 1.15em;
  color: white;

  ${({ $basketLabelDisplayed }) => css`
    font-weight: ${$basketLabelDisplayed ? '500' : 'bold'};
  `}
`;

const OddsLabel = styled(AnimatedDigitsLabel)`
  ${oddsLabelStyle}

  margin-left: 0;
  text-overflow: clip;
  overflow: hidden;
  white-space: nowrap;
  flex: none;
  max-width: 100%;
`;

const BackupOddsLabel = styled.div`
  ${oddsLabelStyle}
`;

const Center = styled.div`
  position: absolute;
  pointer-events: none;
  background: transparent;
  left: 50%;
  top: 50%;
`;

const genericFormatOdds =
  (f, disabled = false) =>
  (odds) => {
    if (odds && !disabled) {
      if (odds < 1_000_000) {
        return f(odds >= 1000 ? Math.floor(odds) : odds);
      }
    }
    return __('Betslip');
  };

type FloatingBasketButtonProps = {
  className?: string;
  totalOdds: number;
  nbBasketItems: number;
  onClick: () => void;
};

const FloatingBasketButton = ({ className = '', totalOdds, nbBasketItems, onClick }: FloatingBasketButtonProps) => {
  const prevTotalOdds = usePrevious(totalOdds);
  const prevNbBasketItems = usePrevious(nbBasketItems);

  const formatOdds = useMemo(
    () => genericFormatOdds(NumberFormatter.formatOddForBasketButtonUsage, !totalOdds && !!nbBasketItems),
    [totalOdds, nbBasketItems],
  );
  const lastFormatOdds = useMemo(
    () => genericFormatOdds(NumberFormatter.formatOdd, !totalOdds && !!nbBasketItems),
    [totalOdds, nbBasketItems],
  );

  const basketLabelDisplayed = lastFormatOdds(totalOdds ? totalOdds : prevTotalOdds) === __('Betslip');

  return (
    <StyleReceiver id="basket-floating-button" className={className} $show={!!nbBasketItems}>
      <Center id="basket-floating-button-center" />
      <Wrapper onClick={onClick} $show={!!nbBasketItems} data-testid="floating-basket-button">
        <NbItemsCounter $n={nbBasketItems ? nbBasketItems : prevNbBasketItems} />
        <BasketIcon />
        {totalOdds ? (
          <OddsLabel
            current={prevTotalOdds ?? 0}
            next={totalOdds}
            timeout={300}
            formatFn={formatOdds}
            lastFormatFn={lastFormatOdds}
            $basketLabelDisplayed={basketLabelDisplayed}
          />
        ) : (
          <BackupOddsLabel $basketLabelDisplayed={basketLabelDisplayed}>
            {lastFormatOdds(prevTotalOdds)}
          </BackupOddsLabel>
        )}
      </Wrapper>
    </StyleReceiver>
  );
};

export default FloatingBasketButton;
